import {
  attachmentSchema,
  buildAttachmentUpdatePayload,
  createEmbedAttachment,
  updateAttachment,
  useGetAttachmentById,
} from "@/api/attachment";
import { uploadImage, uploadVideo, VIDEO_MAX_SIZE_MB } from "@/api/image.ts";
import { useGeneralSettingsFormData } from "@/api/cms";
import { Button } from "@/components/ui/button.tsx";
import {
  FormField,
  FormItem,
} from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import {
  buildAttachmentMetadataOverrides,
  hydrateAttachmentMultilingualFields,
} from "@/lib/media/attachment-multilingual";
import {
  clampDimensionsToMax,
  getImageDimensions,
  MAX_IMAGE_DIMENSION,
  exceedsImageDimensionLimit,
  resampleImage,
} from "@/lib/canvas.ts";
import { Attachment } from "@/lib/types";
import { isVideoFile, VIDEO_DROPZONE_ACCEPT } from "@/lib/media/video";
import { DEFAULT_PLAYER_SETTINGS } from "@/lib/media/embed";
import { markCmsMediaUploaded } from "@/lib/cms/cms-media-upload";
import {
  cmsMediaTranslationKey,
  resolveCmsMediaKindForUpload,
} from "@/lib/cms/cms-media-labels";
import { queryClient } from "@/lib/queryClient";
import { zodResolver } from "@hookform/resolvers/zod";
import { EmbedPlayer } from "@/components/media/EmbedPlayer";
import { ResponsiveVideo } from "@/components/media/ResponsiveVideo";
import { Film, Link2, Loader2, CloudUpload, Aperture } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { MultilingualField } from "../form-items/MultilingualField";
import { Col } from "../form-items/AbstractForm";
import { FileDropzone, FileItem } from "../ui/FileDropzone";
import { Thumbnail } from "../ui/tumbnail";
import { LoadImageDropzoneCta } from "../form-fields/LoadImageDropzoneCta";
import { AcceptedMediaTypesHint } from "@/components/media/AcceptedMediaTypesHint";
import { ImageStudioHost } from "./ImageStudioHost";
import { canRetouchAttachment } from "./can-retouch-attachment";

const IMAGE_ACCEPT: Record<string, string[]> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
};

const isLikelyEmbedUrl = (url: string) =>
  /(?:youtube\.com|youtu\.be|vimeo\.com)/i.test(url.trim());

const imageSchema = attachmentSchema
  .pick({
    textAlt: true,
    caption: true,
    description: true,
  })
  .extend({
    image: z.instanceof(FileList).optional(),
  });

export type ImageFormData = z.infer<typeof imageSchema>;

export type BuildImageNameSignature = (
  instanceName: string,
  docId: string,
  position: string,
  data: ImageFormData,
  /** Fichier en cours d'upload (si présent) — utile pour nommer d'après le fichier. */
  file?: File | null,
) => string;

export type AddCroppedImageFormSuccessMeta = {
  /** True when a new file/embed was uploaded (not metadata-only update). */
  replacedMedia?: boolean;
};

export interface AddCroppedImageFormProps {
  attachment?: Attachment;
  refetch: () => void;
  imagePosition: string;
  aspect?: number;
  onUpdate?: (imageValueElement: any, data: ImageFormData) => void;
  onSuccess?: (
    attachment: Attachment,
    meta?: AddCroppedImageFormSuccessMeta,
  ) => void;
  docId?: string;
  buildImageName: BuildImageNameSignature;
  forceCreateNewAttachment?: boolean;
}

/**
 * Unified CMS media form (no crop — the legacy crop UI lives in
 * ./legacy/AddCroppedImageFormLegacy.tsx). Lets the user, in a single form:
 *  - drop a photo OR a video (type auto-detected),
 *  - or paste a YouTube/Vimeo link (embed),
 *  - and, for images, resize them (WordPress-like: lock width or height).
 * Uploads to the single-slot position (replaces any existing media there).
 */
const AddCroppedImageForm = ({
  attachment,
  refetch,
  imagePosition,
  onUpdate,
  onSuccess,
  docId,
  buildImageName,
  forceCreateNewAttachment = false,
}: AddCroppedImageFormProps) => {
  const { instanceData } = useAuth();
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { data: playerSettings = DEFAULT_PLAYER_SETTINGS } =
    useGeneralSettingsFormData();

  const [file, setFile] = useState<File | null>(null);

  const existingPreviewUrl = (() => {
    if (!attachment) return null;
    if (attachment.kind === "video") {
      return attachment.videoPublicUrl ?? attachment.posterPublicUrl ?? null;
    }
    if (attachment.kind === "embed") return null;
    return attachment.mediumPublicUrl ?? null;
  })();

  const [previewUrl, setPreviewUrl] = useState<string | null>(
    existingPreviewUrl,
  );
  const [isVideo, setIsVideo] = useState(attachment?.kind === "video");
  const [isEmbed, setIsEmbed] = useState(attachment?.kind === "embed");
  const [embedUrl, setEmbedUrl] = useState(attachment?.embedUrl ?? "");
  const [processing, setProcessing] = useState(false);
  const [retouchOpen, setRetouchOpen] = useState(false);
  const [pollingAttachmentId, setPollingAttachmentId] = useState<string | null>(
    null,
  );

  const { data: polledAttachment, refetch: refetchPolledAttachment } =
    useGetAttachmentById(pollingAttachmentId ?? "", {
      pollWhileProcessing: true,
    });

  const resolvedAttachment = useMemo(() => {
    if (polledAttachment) return polledAttachment as Attachment;
    return attachment;
  }, [polledAttachment, attachment]);

  const isVideoProcessing =
    resolvedAttachment?.kind === "video" &&
    resolvedAttachment.videoStatus === "processing";

  useEffect(() => {
    if (file) return;
    setIsVideo(attachment?.kind === "video");
    setIsEmbed(attachment?.kind === "embed");
    setEmbedUrl(attachment?.embedUrl ?? "");
    if (attachment?.kind === "video") {
      setPreviewUrl(
        attachment.videoPublicUrl ?? attachment.posterPublicUrl ?? null,
      );
    } else if (attachment?.kind === "embed") {
      setPreviewUrl(null);
    } else {
      setPreviewUrl(attachment?.mediumPublicUrl ?? null);
    }
  }, [attachment, file]);

  useEffect(() => {
    if (
      !polledAttachment?.id ||
      polledAttachment.id !== pollingAttachmentId ||
      polledAttachment.videoStatus !== "ready"
    ) {
      return;
    }
    setPreviewUrl(
      polledAttachment.videoPublicUrl ??
        polledAttachment.posterPublicUrl ??
        null,
    );
    setIsVideo(true);
    onSuccess?.(polledAttachment as Attachment);
    void refetch();
    setPollingAttachmentId(null);
  }, [polledAttachment, pollingAttachmentId, onSuccess, refetch]);

  // WordPress-like resize state (images only).
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [targetWidth, setTargetWidth] = useState<number>(0);
  const [targetHeight, setTargetHeight] = useState<number>(0);

  const formImage = useForm<ImageFormData>({
    resolver: zodResolver(imageSchema),
    defaultValues: {
      caption: attachment?.caption,
      textAlt: attachment?.textAlt,
      description: attachment?.description,
    },
  });
  const { handleSubmit, control, reset, formState } = formImage;
  const { dirtyFields } = formState;

  useEffect(() => {
    const source = resolvedAttachment ?? attachment;
    if (!source) return;
    const multilingual = hydrateAttachmentMultilingualFields(source);
    reset({
      caption: multilingual.caption,
      textAlt: multilingual.textAlt,
      description: multilingual.description,
    });
  }, [resolvedAttachment?.id, attachment?.id, reset]);

  const resolvedKind =
    resolvedAttachment?.kind ?? attachment?.kind ?? (isVideo ? "video" : isEmbed ? "embed" : "image");

  const activeMediaKind = useMemo(
    () =>
      resolveCmsMediaKindForUpload({
        file,
        embedUrl,
        isVideo,
        isEmbed,
        attachment: resolvedAttachment ?? attachment,
      }),
    [file, embedUrl, isVideo, isEmbed, resolvedAttachment, attachment],
  );

  const onSelectFile = async (picked: File | undefined) => {
    if (!picked) return;
    setEmbedUrl("");
    setIsEmbed(false);
    const video = isVideoFile(picked);
    setIsVideo(video);
    setFile(picked);
    const url = URL.createObjectURL(picked);
    setPreviewUrl(url);

    if (!video) {
      try {
        const dim = await getImageDimensions(url);
        setNaturalSize(dim);
        setTargetWidth(dim.width);
        setTargetHeight(dim.height);
      } catch {
        setNaturalSize(null);
      }
    } else {
      setNaturalSize(null);
    }
  };

  // Keep aspect ratio locked while editing width/height.
  const onWidthChange = (value: number) => {
    if (!naturalSize) return;
    const ratio = naturalSize.width / naturalSize.height;
    const w = Math.max(1, Math.min(naturalSize.width, value || 0));
    setTargetWidth(w);
    setTargetHeight(Math.round(w / ratio));
  };
  const onHeightChange = (value: number) => {
    if (!naturalSize) return;
    const ratio = naturalSize.width / naturalSize.height;
    const h = Math.max(1, Math.min(naturalSize.height, value || 0));
    setTargetHeight(h);
    setTargetWidth(Math.round(h * ratio));
  };

  const resetSize = () => {
    if (!naturalSize) return;
    setTargetWidth(naturalSize.width);
    setTargetHeight(naturalSize.height);
  };

  /**
   * Brings the output back under the API limit in one click: the edge that
   * overflows is pinned to MAX_IMAGE_DIMENSION and the other follows the
   * aspect ratio.
   */
  const autoResizeToLimit = () => {
    if (!naturalSize) return;
    const clamped = clampDimensionsToMax(targetWidth, targetHeight);
    setTargetWidth(clamped.width);
    setTargetHeight(clamped.height);
  };

  const isImageOutputOversized =
    !!naturalSize &&
    !isVideo &&
    exceedsImageDimensionLimit(targetWidth, targetHeight);

  const hasResizedOutput =
    !!naturalSize &&
    (targetWidth !== naturalSize.width ||
      targetHeight !== naturalSize.height);

  /** Returns the (possibly resized) image file to upload. */
  const buildImageFileToUpload = async (): Promise<File> => {
    if (!file || !previewUrl || !naturalSize) return file as File;
    const unchanged =
      targetWidth === naturalSize.width &&
      targetHeight === naturalSize.height;
    if (unchanged) return file;
    return resampleImage(
      previewUrl,
      { lockWidth: targetWidth },
      file.name,
    );
  };

  const submit = async (data: ImageFormData) => {
    if (instanceData == null) {
      console.error("Tried to upload media, but instanceData is null");
      return;
    }
    const effectiveDocId = docId ?? instanceData.id;
    const name =
      buildImageName?.(
        instanceData.name,
        effectiveDocId,
        imagePosition,
        data,
        file,
      ) ?? "";
    const trimmedEmbedUrl = embedUrl.trim();
    const embedUrlChanged =
      !!trimmedEmbedUrl &&
      trimmedEmbedUrl !== (resolvedAttachment?.embedUrl ?? attachment?.embedUrl ?? "");

    try {
      setProcessing(true);

      // 1) New or changed YouTube/Vimeo embed URL
      if (trimmedEmbedUrl && (embedUrlChanged || !resolvedAttachment?.id)) {
        if (!isLikelyEmbedUrl(trimmedEmbedUrl)) {
          toast({
            title: "Error",
            description: t("mediaLibrary.embed.invalidUrl"),
            variant: "error",
            duration: 2500,
          });
          return;
        }
        const created = await createEmbedAttachment({
          url: trimmedEmbedUrl,
          docId: effectiveDocId,
          position: imagePosition,
          instanceId: instanceData.id,
          unique: !forceCreateNewAttachment,
          caption: data.caption,
          textAlt: data.textAlt,
          description: data.description,
        });
        toast({
          variant: "success",
          description: t(cmsMediaTranslationKey("uploadSuccess", "embed")),
          duration: 1000,
        });
        markCmsMediaUploaded("embed");
        refetch();
        onSuccess?.(created as unknown as Attachment, { replacedMedia: true });
        return;
      }

      // 2) New file → replace image or video
      if (file) {
        if (isVideo) {
          if (file.size > VIDEO_MAX_SIZE_MB * 1024 * 1024) {
            toast({
              title: "Error",
              description: t("mediaLibrary.video.tooLarge", {
                max: VIDEO_MAX_SIZE_MB,
              }),
              variant: "error",
              duration: 4000,
            });
            return;
          }
          const placeholder = await uploadVideo(
            file,
            {
              name,
              displayName: name || undefined,
              docId: effectiveDocId,
              position: imagePosition,
              instanceId: instanceData.id,
              unique: !forceCreateNewAttachment,
              caption: data.caption,
              textAlt: data.textAlt,
              description: data.description,
            },
            instanceData.s3Bucket,
          );
          setPollingAttachmentId(placeholder.id);
          setFile(null);
          setPreviewUrl(
            placeholder.posterPublicUrl ?? placeholder.videoPublicUrl ?? null,
          );
          toast({
            variant: "success",
            description: t(cmsMediaTranslationKey("uploadSuccess", "video")),
            duration: 1000,
          });
          markCmsMediaUploaded("video");
          await refetch();
          void refetchPolledAttachment();
          onSuccess?.(placeholder as unknown as Attachment, {
            replacedMedia: true,
          });
          return;
        }

        if (
          exceedsImageDimensionLimit(targetWidth, targetHeight)
        ) {
          toast({
            title: t("error"),
            description: t("addImage.resize.tooLarge", {
              max: MAX_IMAGE_DIMENSION,
            }),
            variant: "error",
            duration: 4000,
          });
          return;
        }

        const imageFile = await buildImageFileToUpload();
        if (onUpdate) {
          onUpdate(imageFile, data);
          return;
        }
        const created = await uploadImage(
          imageFile,
          {
            ...data,
            details: JSON.stringify({
              width: targetWidth,
              height: targetHeight,
            }),
            name,
            docId: effectiveDocId,
            position: imagePosition,
            published: false,
            instanceId: instanceData.id,
          },
          instanceData.s3Bucket,
          instanceData.id,
          forceCreateNewAttachment ? "back/api/attachment/create" : undefined,
          // displayName : le create API le mappe sur `name` (prioritaire).
          name || undefined,
        );
        toast({
          variant: "success",
          description: t(cmsMediaTranslationKey("uploadSuccess", "image")),
          duration: 1000,
        });
        markCmsMediaUploaded("image");
        refetch();
        onSuccess?.(created, { replacedMedia: true });
        return;
      }

      // 3) Existing media — update metadata only (caption, alt, description)
      const existing = resolvedAttachment ?? attachment;
      if (existing?.id) {
        const hydrated = hydrateAttachmentMultilingualFields(existing);
        const metadataOverrides = buildAttachmentMetadataOverrides(
          existing,
          {
            caption: data.caption,
            textAlt: data.textAlt,
            description: data.description,
          },
          dirtyFields,
        );
        const updated = await updateAttachment({
          id: existing.id,
          attachment: buildAttachmentUpdatePayload(
            { ...existing, ...hydrated },
            metadataOverrides,
          ),
        });
        await queryClient.invalidateQueries({ queryKey: ["attachment"] });
        const existingKind = resolveCmsMediaKindForUpload({ attachment: existing });
        toast({
          variant: "success",
          description: t(cmsMediaTranslationKey("updateSuccess", existingKind)),
          duration: 1000,
        });
        refetch();
        onSuccess?.(updated as Attachment);
        return;
      }

      toast({
        title: "Error",
        description: t(cmsMediaTranslationKey("uploadError", activeMediaKind)),
        variant: "error",
        duration: 1000,
      });
    } catch (e) {
      console.error(e);
      toast({
        title: "Error",
        description: t(cmsMediaTranslationKey("uploadError", activeMediaKind)),
        variant: "error",
        duration: 1500,
      });
    } finally {
      setProcessing(false);
    }
  };

  const displayEmbedUrl =
    embedUrl.trim() ||
    resolvedAttachment?.embedUrl ||
    attachment?.embedUrl ||
    "";
  const showEmbedPlayer =
    (resolvedKind === "embed" || isEmbed) &&
    (Boolean(displayEmbedUrl) ||
      Boolean(
        (resolvedAttachment?.embedVideoId ?? attachment?.embedVideoId) &&
          (resolvedAttachment?.embedProvider ?? attachment?.embedProvider),
      ));

  return (
    <div className="flex flex-col gap-6 w-full min-w-0 max-w-full">
      {/* Preview: embed player, uploaded video, or existing image */}
      {showEmbedPlayer ? (
        <EmbedPlayer
          embedUrl={displayEmbedUrl}
          provider={resolvedAttachment?.embedProvider ?? attachment?.embedProvider}
          videoId={resolvedAttachment?.embedVideoId ?? attachment?.embedVideoId}
          portraitMaxHeight="85dvh"
          className="w-full"
        />
      ) : isVideoProcessing ? (
        <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground border border-dashed rounded-md p-6 w-full min-h-[180px]">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p className="text-sm text-center">
            {t("mediaLibrary.video.processing")}
          </p>
        </div>
      ) : !file && previewUrl ? (
        isVideo ? (
          <ResponsiveVideo
            src={previewUrl}
            poster={resolvedAttachment?.posterPublicUrl ?? undefined}
            loop={playerSettings.playerLoop}
            portraitMaxHeight="85dvh"
          />
        ) : (
          <div className="relative">
          <img
            src={previewUrl}
            alt="media"
            className="w-full max-h-[360px] object-contain bg-gray-50 rounded-md"
          />
          {canRetouchAttachment(resolvedAttachment ?? attachment) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="absolute bottom-2 right-2 shadow-sm"
              onClick={() => setRetouchOpen(true)}
            >
              <Aperture className="mr-2 h-4 w-4" />
              {vt("imageStudio.retouch", "Retoucher")}
            </Button>
          )}
          </div>
        )
      ) : null}

      {/* Dropzone (image OR video) — hidden while an embed URL is active */}
      {!embedUrl.trim() && (
        <div className="flex flex-col gap-3">
          {file ? (
            <FileItem
              file={file}
              image={
                isVideo ? (
                  <Film className="w-4 h-4 text-gray-500" />
                ) : (
                  <img
                    src={previewUrl ?? ""}
                    alt=""
                    className="w-10 h-10 object-cover rounded"
                  />
                )
              }
              onDelete={() => {
                setFile(null);
                setNaturalSize(null);
                const existing = resolvedAttachment ?? attachment;
                if (existing?.kind === "video") {
                  setIsVideo(true);
                  setPreviewUrl(
                    existing.videoPublicUrl ?? existing.posterPublicUrl ?? null,
                  );
                } else if (existing?.kind === "embed") {
                  setIsVideo(false);
                  setPreviewUrl(null);
                } else if (existing) {
                  setIsVideo(false);
                  setPreviewUrl(existing.mediumPublicUrl ?? null);
                } else {
                  setIsVideo(false);
                  setPreviewUrl(null);
                }
              }}
            />
          ) : (
            <div className="flex flex-col gap-2">
              {(resolvedAttachment ?? attachment) && (
                <p className="text-xs text-muted-foreground text-center">
                  {t("loadMedia.replaceHint")}
                </p>
              )}
              <FileDropzone
              multiple={false}
              accept={{ ...IMAGE_ACCEPT, ...VIDEO_DROPZONE_ACCEPT }}
              onDrop={(f: File[]) => onSelectFile(f[0])}
              className="bg-white border border-solid"
            >
              {() => (
                <div className="flex flex-col items-center justify-center gap-6">
                  <Thumbnail>
                    <CloudUpload className="text-gray-700 size-6" />
                  </Thumbnail>
                  <LoadImageDropzoneCta dragCtaKey="loadImage.dragCtaSingle" />
                  <AcceptedMediaTypesHint />
                </div>
              )}
            </FileDropzone>
            </div>
          )}

          {/* WordPress-like resize tool (images only) */}
          {file && !isVideo && naturalSize && (
            <div className="flex flex-col gap-2 border border-gray-200 rounded-md p-3 bg-gray-50">
              <p className="text-sm font-semibold">
                {t("addImage.resize.title")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("addImage.dimensions", {
                  width: naturalSize.width,
                  height: naturalSize.height,
                })}
              </p>
              {(naturalSize.width > MAX_IMAGE_DIMENSION ||
                naturalSize.height > MAX_IMAGE_DIMENSION) && (
                <p className="text-xs text-amber-700">
                  {t("addImage.resize.tooLarge", {
                    max: MAX_IMAGE_DIMENSION,
                  })}
                </p>
              )}
              {hasResizedOutput && (
                <p className="text-xs text-muted-foreground">
                  {t("addImage.resize.outputDimensions", {
                    width: targetWidth,
                    height: targetHeight,
                  })}
                </p>
              )}
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col text-xs gap-1">
                  {t("addImage.resize.width")}
                  <Input
                    type="number"
                    min={1}
                    max={naturalSize.width}
                    value={targetWidth}
                    onChange={(e) => onWidthChange(Number(e.target.value))}
                    className="w-28"
                  />
                </label>
                <span className="pb-2">×</span>
                <label className="flex flex-col text-xs gap-1">
                  {t("addImage.resize.height")}
                  <Input
                    type="number"
                    min={1}
                    max={naturalSize.height}
                    value={targetHeight}
                    onChange={(e) => onHeightChange(Number(e.target.value))}
                    className="w-28"
                  />
                </label>
                <Button type="button" variant="outline" onClick={resetSize}>
                  {t("addImage.resize.reset")}
                </Button>
                {isImageOutputOversized && (
                  <Button type="button" onClick={autoResizeToLimit}>
                    {t("addImage.resize.auto", {
                      max: MAX_IMAGE_DIMENSION,
                      defaultValue: `Redimensionner à ${MAX_IMAGE_DIMENSION} px (addImage.resize.auto)`,
                    })}
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("addImage.resize.hint")}
              </p>
            </div>
          )}
        </div>
      )}

      {/* OR a YouTube / Vimeo link (embed) */}
      {!file && (
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium flex items-center gap-1">
            <Link2 className="w-4 h-4" />
            {t("mediaLibrary.embed.urlLabel")}
          </label>
          <Input
            placeholder="https://www.youtube.com/watch?v=..."
            value={embedUrl}
            onChange={(e) => {
              const value = e.target.value;
              setEmbedUrl(value);
              if (value.trim()) {
                setFile(null);
                setPreviewUrl(null);
                setIsVideo(false);
                setIsEmbed(true);
              } else {
                setIsEmbed(false);
              }
            }}
          />
        </div>
      )}

      <FormProvider {...formImage}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className="w-full flex flex-col gap-3"
        >
          <MultilingualField
            render={(lang) => (
              <Col>
                <FormField
                  control={control}
                  name={`textAlt.${lang}`}
                  render={({ field }) => (
                    <FormItem>
                      <Input
                        {...field}
                        label={t("addImageTextAltFR")}
                        id={field.name}
                        type="text"
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`caption.${lang}`}
                  render={({ field }) => (
                    <FormItem>
                      <Input
                        {...field}
                        label={t("addImageLegendFR")}
                        id={field.name}
                        type="text"
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name={`description.${lang}`}
                  render={({ field }) => (
                    <FormItem>
                      <Input
                        {...field}
                        label={t("addImageDescriptionFR")}
                        id={field.name}
                        type="text"
                      />
                    </FormItem>
                  )}
                />
              </Col>
            )}
          />

          {processing && isVideo && (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              {t("mediaLibrary.video.processing")}
            </p>
          )}

          {isVideoProcessing && !processing && (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              {t("mediaLibrary.video.processing")}
            </p>
          )}

          <Button
            type="button"
            disabled={processing || isImageOutputOversized}
            onClick={(e) => {
              e.stopPropagation();
              void handleSubmit(submit)();
            }}
          >
            {processing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t(cmsMediaTranslationKey("submit", activeMediaKind))
            )}
          </Button>
        </form>
      </FormProvider>
      <ImageStudioHost
        attachment={(resolvedAttachment ?? attachment) as Attachment | null}
        open={retouchOpen && canRetouchAttachment(resolvedAttachment ?? attachment)}
        onOpenChange={setRetouchOpen}
        onSaved={() => {
          void refetch();
        }}
      />
    </div>
  );
};

export default AddCroppedImageForm;
